package com.milta.feature.route.presentation

import com.milta.core.mvi.MviViewModel
import com.milta.core.network.ApiException
import com.milta.feature.route.domain.usecase.GetRouteDetailUseCase
import com.milta.feature.route.domain.usecase.GetRouteProofsUseCase
import com.milta.feature.route.domain.usecase.GetTeamRoutesOverviewUseCase
import kotlinx.coroutines.CoroutineDispatcher
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.launch
import kotlinx.datetime.DateTimeUnit
import kotlinx.datetime.TimeZone
import kotlinx.datetime.plus
import kotlinx.datetime.todayIn
import kotlin.time.Clock

/**
 * ViewModel for the web dashboard's Supervision screen (MIL-48, DOM-06). Reuses the exact same
 * `GET /routes`/`GET /routes/{id}` use cases as the mobile Manager dashboard
 * ([GestionnaireDashboardViewModel]/[GestionnaireRouteDetailViewModel]) — nothing new added to the
 * domain/data layers.
 *
 * Snapshot-only by explicit PO decision: unlike [GestionnaireDashboardViewModel], this ViewModel
 * never opens a [com.milta.core.network.RouteRealtimeClient] connection and never polls — the
 * dashboard reflects the backend's state as of page load / a manual [WebDashboardIntent.Refresh]
 * only.
 *
 * "Tomorrow" date computed with the exact same kotlinx-datetime mechanism already used for date
 * toggles elsewhere (see [DriverHomeViewModel.tomorrowIso]/[GestionnaireDashboardViewModel.yesterdayIso]) —
 * no new date-arithmetic invented here.
 */
class WebDashboardViewModel(
    private val getTeamRoutesOverview: GetTeamRoutesOverviewUseCase,
    private val getRouteDetail: GetRouteDetailUseCase,
    private val getRouteProofs: GetRouteProofsUseCase,
    // Runs the ViewModel's coroutines (see MviViewModel): tests pass the runTest scheduler's dispatcher.
    dispatcher: CoroutineDispatcher = Dispatchers.Default,
) : MviViewModel<WebDashboardState, WebDashboardIntent>(WebDashboardState(), dispatcher) {

    private companion object {
        // Same rationale as StopDetailViewModel's mobile counterpart: finds one delivery's proof
        // among a route's completed/failed stops, not a real pagination need.
        const val PROOF_LOOKUP_PAGE_SIZE = 100
    }

    init {
        load()
    }

    override fun onIntent(intent: WebDashboardIntent) {
        when (intent) {
            WebDashboardIntent.LoadToday -> switchTab(WebDashboardTab.Today)
            WebDashboardIntent.LoadTomorrow -> switchTab(WebDashboardTab.Tomorrow)
            is WebDashboardIntent.SwitchTab -> switchTab(intent.tab)
            WebDashboardIntent.Refresh -> load()
            is WebDashboardIntent.ExpandRoute -> toggleExpand(intent.routeId)
            is WebDashboardIntent.ShowProof -> showProof(intent.deliveryId)
            WebDashboardIntent.DismissProof -> setState {
                copy(proofDialogDeliveryId = null, isLoadingProof = false, proofPhotoUrl = null, proofComment = null, proofFailureReason = null, proofLoadFailed = false, proofWithoutPhoto = false)
            }
            is WebDashboardIntent.SetStatusFilter -> setState { copy(statusFilter = intent.status) }
            is WebDashboardIntent.SetDriverFilter -> setState { copy(driverFilter = intent.driverId) }
        }
    }

    /** [currentState.expandedRouteId] is guaranteed non-null here: [WebDashboardIntent.ShowProof] is
     * only ever dispatched from a stop row rendered inside the already-expanded route's detail. */
    private fun showProof(deliveryId: String) {
        val routeId = currentState.expandedRouteId ?: return
        setState { copy(proofDialogDeliveryId = deliveryId, isLoadingProof = true, proofPhotoUrl = null, proofComment = null, proofFailureReason = null, proofLoadFailed = false, proofWithoutPhoto = false) }
        scope.launch {
            runCatching { getRouteProofs(routeId, page = 1, pageSize = PROOF_LOOKUP_PAGE_SIZE) }
                .onSuccess { result ->
                    val proof = result.proofs.items.firstOrNull { it.deliveryId == deliveryId }
                    setState {
                        copy(
                            isLoadingProof = false,
                            proofPhotoUrl = proof?.proofPhotoUrl,
                            proofComment = proof?.comment,
                            proofFailureReason = proof?.failureReason,
                            proofWithoutPhoto = proof != null && proof.proofPhotoUrl == null,
                        )
                    }
                }
                .onFailure {
                    setState { copy(isLoadingProof = false, proofLoadFailed = true) }
                }
        }
    }

    private fun switchTab(tab: WebDashboardTab) {
        if (tab == currentState.selectedTab) return
        // Collapse any expanded route detail — it belongs to the previous day's overview. Also
        // clears the status/driver filter (MIL-85): a filter value meaningful for today's drivers
        // could silently hide every route on tomorrow's list otherwise.
        setState {
            copy(
                selectedTab = tab,
                expandedRouteId = null,
                expandedRoute = null,
                routeDetailError = null,
                statusFilter = null,
                driverFilter = null,
            )
        }
        load()
    }

    private fun load() {
        val date = if (currentState.selectedTab == WebDashboardTab.Tomorrow) tomorrowIso() else null
        scope.launch {
            setState { copy(isLoading = true, error = null) }
            runCatching { getTeamRoutesOverview(date) }.fold(
                onSuccess = { overview -> setState { copy(isLoading = false, overview = overview) } },
                onFailure = { e -> setState { copy(isLoading = false, error = mapError(e)) } },
            )
        }
    }

    /** Re-clicking the already-expanded route collapses it without a re-fetch; otherwise loads its
     * detail (`GET /routes/{id}`) and expands it. */
    private fun toggleExpand(routeId: String) {
        if (currentState.expandedRouteId == routeId) {
            setState { copy(expandedRouteId = null, expandedRoute = null, routeDetailError = null) }
            return
        }
        setState { copy(expandedRouteId = routeId, expandedRoute = null, routeDetailError = null) }
        scope.launch {
            setState { copy(isLoadingRouteDetail = true) }
            runCatching { getRouteDetail(routeId) }.fold(
                onSuccess = { route -> setState { copy(isLoadingRouteDetail = false, expandedRoute = route) } },
                onFailure = { e ->
                    setState { copy(isLoadingRouteDetail = false, routeDetailError = mapRouteDetailError(e)) }
                },
            )
        }
    }

    private fun tomorrowIso(): String =
        Clock.System.todayIn(TimeZone.currentSystemDefault()).plus(1, DateTimeUnit.DAY).toString()

    private fun mapError(e: Throwable): String = when {
        e is ApiException && e.statusCode == 400 -> "invalid_date"
        e is ApiException -> "server_error"
        else -> "offline"
    }

    /** Mirrors [GestionnaireRouteDetailViewModel.errorCode] exactly. */
    private fun mapRouteDetailError(e: Throwable): String = when {
        e is ApiException && e.statusCode == 404 -> "route_not_found"
        e is ApiException && e.statusCode == 403 -> "forbidden"
        e !is ApiException -> "offline"
        else -> "server_error"
    }
}
